"use strict";
/**
 * Order Creation
 * Creates order requests from app state
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.partialSellQuantity = partialSellQuantity;
exports.createOrderRequest = createOrderRequest;
const state_1 = require("../state");
const sport_mode_1 = require("../ui/sport-mode");
const orderbook_utils_1 = require("./orderbook-utils");
const slippage_utils_1 = require("./slippage-utils");
const permissions_1 = require("../permissions");
const displayed_ask_history_1 = require("./displayed-ask-history");
/** Partial mode asks for the fixed amount; the server caps it at what it holds (ai/POSITION_CACHE_PLAN.md §5a). */
function partialSellQuantity(settings) {
    return settings.sellMode === "partial" && settings.fixedSellQuantity > 0 ? settings.fixedSellQuantity : null;
}
/**
 * Create order request from app state
 * OPTIMIZED: Uses cached bid/ask values to avoid sorting on every order (saves 3-5ms)
 * Supports Manual Price Mode: when active, uses user-defined limit prices instead of orderbook
 * @param marketId - Market ticker
 * @param side - yes or no
 * @param action - buy or sell
 * @param options - Optional price / slippage overrides (baseball mode)
 * @returns Order request object
 */
function createOrderRequest(marketId, side, action = "buy", options = {}) {
    const appState = (0, state_1.getState)();
    const { tradingSettings } = appState;
    // Close-opposite is owner-gated (server-enforced too); force it off when the
    // tier can't use it so the client never sends a close the server would zero.
    const closeOppositeOnBuy = tradingSettings.closeOppositeOnBuy !== false &&
        (0, permissions_1.permsAllowCloseOpposite)(appState.permissions);
    const orderDollarAmount = options.dollarAmount ?? tradingSettings.dollarAmount;
    // Tag fanned-out additional-market orders so the server can gate them
    // (allowAdditionalMarkets). Set only by the additional-market fan-out.
    const additionalMarket = options.additionalMarket === true;
    // Tag fanned-out parallel-market orders so the server can gate them
    // (allowParallelMarkets). Set only by the parallel-market fan-out.
    const parallelMarket = options.parallelMarket === true;
    // Round fractional position counts to 2 decimal places to prevent float
    // residuals (e.g. 10.300000000001) from leaking into the order request.
    const round2 = (n) => Math.round(n * 100) / 100;
    // The server sizes sells and the close from its own count
    // (order-builders.ts heldCount). A sell still needs a position on screen;
    // a leg never closes opposite.
    let sellCount = 0;
    if (action === "sell") {
        const onScreen = round2((side === "yes" ? appState.currentPosition?.yes_quantity : appState.currentPosition?.no_quantity) || 0);
        if (onScreen <= 0)
            throw new Error(`No ${side.toUpperCase()} position to sell`);
        sellCount = partialSellQuantity(tradingSettings) ?? onScreen;
    }
    const closeOpposite = action === "buy" && closeOppositeOnBuy && !parallelMarket && !additionalMarket;
    // ============================================================================
    // Per-button baseball manual price override (combined baseball + manual mode)
    // ============================================================================
    if (options.manualPrice !== undefined) {
        const price = options.manualPrice;
        const priceDollars = price / 100;
        const share_count = action === "buy"
            ? Math.floor(orderDollarAmount / priceDollars)
            : 0;
        return {
            ticker: marketId,
            target_side: side,
            action,
            closeOpposite,
            dollar_amount: action === "buy" ? orderDollarAmount : 0,
            yes_ask: side === "yes" ? price : 0,
            no_ask: side === "no" ? price : 0,
            max_slippage_pts: 0,
            current_position_count: sellCount,
            min_shares: action === "buy" ? share_count : 0,
            useDelayedPrice: false,
            manualLimitPrice: price,
            additionalMarket,
            parallelMarket,
            clickId: options.clickId,
            sellMode: action === "sell" ? tradingSettings.sellMode : undefined,
        };
    }
    // ============================================================================
    // Manual Price Mode: Use user-defined limit prices, bypass orderbook + slippage
    // When a sport layout (baseball or basketball) is showing, per-button prices
    // are used (via options.manualPrice) or the regular slippage path is used
    // (MISC/EXT buttons and sells), so the global manual-price guard does not
    // apply. Under forceDefaultButtons the traditional layout shows, so it does.
    // ============================================================================
    if (tradingSettings.manualPriceMode && !(0, sport_mode_1.isSportLayoutActive)(tradingSettings)) {
        const manualPriceSelected = side === "yes"
            ? tradingSettings.manualYesPriceSelected === true
            : tradingSettings.manualNoPriceSelected === true;
        if (!manualPriceSelected) {
            throw new Error(`Select a manual ${side.toUpperCase()} price before placing an order`);
        }
        const manualPrice = side === "yes"
            ? tradingSettings.manualYesPrice
            : tradingSettings.manualNoPrice;
        // Manual price is 1-99 integer (cents); compute dollar amount-based share count
        const priceDollars = manualPrice / 100;
        const share_count_to_send = action === "buy"
            ? Math.floor(orderDollarAmount / priceDollars)
            : 0;
        return {
            ticker: marketId,
            target_side: side,
            action,
            closeOpposite,
            dollar_amount: action === "buy" ? orderDollarAmount : 0,
            yes_ask: side === "yes" ? manualPrice : 0,
            no_ask: side === "no" ? manualPrice : 0,
            max_slippage_pts: 0, // No slippage in manual mode
            current_position_count: sellCount,
            min_shares: action === "buy" ? share_count_to_send : 0,
            useDelayedPrice: false,
            manualLimitPrice: manualPrice,
            additionalMarket,
            parallelMarket,
            clickId: options.clickId,
            sellMode: action === "sell" ? tradingSettings.sellMode : undefined,
        };
    }
    // ============================================================================
    // Auto Mode: Use cached bid/ask values from orderbook
    // Performance Optimization: Avoids sorting orderbook arrays on every order (saves 3-5ms)
    // ============================================================================
    const bidAsk = appState.cachedBidAsk;
    // Fast path: Use cached values (already sorted and parsed)
    let yes_ask, no_ask;
    if (bidAsk) {
        // One-sided book: buying a side with no resting asks cannot be priced.
        const sideAsk = side === "yes" ? bidAsk.yesAsk : bidAsk.noAsk;
        if (sideAsk === null || sideAsk <= 0) {
            throw new Error(`No ask liquidity available for ${side} side`);
        }
        yes_ask = bidAsk.yesAsk ?? 0;
        no_ask = bidAsk.noAsk ?? 0;
    }
    else {
        // Slow path: Compute from orderbook (fallback - shouldn't happen in normal flow)
        if (!appState.orderBook) {
            throw new Error("Orderbook data not available");
        }
        const computed = (0, orderbook_utils_1.getBidsAndAsks)(appState.orderBook);
        yes_ask = computed.yes_ask;
        no_ask = computed.no_ask;
    }
    const share_count_to_send = Math.floor(orderDollarAmount / (side === "yes" ? yes_ask : no_ask));
    const effectiveSlippage = options.overrideSlippage !== undefined
        ? options.overrideSlippage
        : (0, slippage_utils_1.getEffectiveSlippage)(tradingSettings, side);
    // The ask displayed ~one reaction time before this click, with its real age.
    // Null for a market this client isn't rendering (a fanned-out order on
    // another ticker), which drops the field and lets the server use the book.
    const seen = (0, displayed_ask_history_1.getDisplayedAsk)(marketId, side);
    return {
        ticker: marketId,
        target_side: side,
        action,
        closeOpposite,
        dollar_amount: action === "buy" ? orderDollarAmount : 0,
        yes_ask,
        no_ask,
        max_slippage_pts: effectiveSlippage,
        current_position_count: sellCount,
        min_shares: action === "buy" ? share_count_to_send : 0,
        // Delayed pricing is a trusted-tier feature; force T0 (real-time) off when
        // the tier can't use it so a stale persisted preference never leaks through.
        useDelayedPrice: tradingSettings.useDelayedPrice === true &&
            (0, permissions_1.permsAllowDelayedPrice)(appState.permissions),
        // Undefined keys drop out of the JSON body.
        ask_seen: seen?.price,
        ask_seen_age_ms: seen?.ageMs,
        additionalMarket,
        parallelMarket,
        clickId: options.clickId,
        sellMode: action === "sell" ? tradingSettings.sellMode : undefined,
    };
}
